import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { formatInr } from "./runPayrollUtils";
import { salaryStructureService, branchService } from "../../../services/api";
import { templatesToStructures } from "../../../utils/payrollSalaryBreakdownEnrichment";
import {
  buildSalaryRevisionArrearsComponents,
  sumArrearsDayFactor,
  resolveMonthArrearsFactor,
} from "../../../utils/salaryRevisionArrearsComponents";
import { getAuthJson } from "../../../utils/authStorage";

function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return formatInr(n);
}

function signedClass(n, darkMode) {
  const v = Number(n) || 0;
  if (v > 0) return darkMode ? "text-emerald-400" : "text-emerald-700";
  if (v < 0) return darkMode ? "text-red-400" : "text-red-700";
  return "";
}

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/** Map Process arrearsDetail component rows → preview section shape. */
function sectionForBackendType(type, name) {
  const t = String(type || "").toLowerCase();
  const n = String(name || "").toLowerCase();
  if (/deduct/.test(t) || /professional\s*tax|\bpt\b/.test(n) || /^pf\s*\(\s*employee/.test(n)) {
    return "Deductions (D)";
  }
  if (
    /employer|benefit/.test(t) ||
    /gratuity/.test(n) ||
    /^pf\s*\(\s*employer/.test(n)
  ) {
    return "Employer contributions (B)";
  }
  return "Earnings (E)";
}

function mapBackendComponents(list) {
  return (Array.isArray(list) ? list : [])
    .map((c) => {
      const name = String(c.name || c.componentName || "").trim();
      if (!name) return null;
      const section = sectionForBackendType(c.type || c.category, name);
      return {
        name,
        section,
        type: section,
        present: Number(c.present) || 0,
        revised: Number(c.revised) || 0,
        differential: Number(c.differential) || 0,
        presentMonthly: Number(c.presentMonthly) || 0,
        revisedMonthly: Number(c.revisedMonthly) || 0,
      };
    })
    .filter(Boolean);
}

/** 2026-06 → Jun-26 */
function formatPayMonthShort(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s || "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = Number(m[2]) - 1;
  const yy = String(m[1]).slice(-2);
  return `${months[idx] || m[2]}-${yy}`;
}

/**
 * Eye-view: vertical Present / Revised / Differential for every assigned structure component
 * (same engine as Assigned Structure / Salary Structure modal), plus month-wise & consolidated.
 */
export default function SalaryRevisionArrearsBreakdownModal({
  darkMode = false,
  row,
  onClose,
  structures: structuresProp = null,
  statutoryConfigJson: statutoryProp = null,
  employees: employeesProp = null,
  branchesList: branchesProp = null,
  orgSalaryMethod = null,
}) {
  const detail = row?.arrearsDetail || null;
  const [loading, setLoading] = useState(true);
  const [structures, setStructures] = useState(structuresProp || []);
  const [statutoryConfigJson, setStatutoryConfigJson] = useState(statutoryProp);
  const [branchesList, setBranchesList] = useState(branchesProp || []);
  const [loadError, setLoadError] = useState(null);
  const [activeTemplateId, setActiveTemplateId] = useState(
    detail?.templateId != null ? Number(detail.templateId) : null
  );
  const [statutoryFromAssignment, setStatutoryFromAssignment] = useState(
    detail?.statutoryApplicability || null
  );
  const [monthTab, setMonthTab] = useState("all");

  useEffect(() => {
    if (Array.isArray(structuresProp) && structuresProp.length) {
      setStructures(structuresProp);
    }
  }, [structuresProp]);

  useEffect(() => {
    if (statutoryProp) setStatutoryConfigJson(statutoryProp);
  }, [statutoryProp]);

  useEffect(() => {
    if (Array.isArray(branchesProp)) setBranchesList(branchesProp);
  }, [branchesProp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const needStructures = !(Array.isArray(structuresProp) && structuresProp.length);
      const needStatutory = statutoryProp == null;
      const needBranches = !(Array.isArray(branchesProp) && branchesProp.length);
      if (!needStructures && !needStatutory && !needBranches) {
        if (!cancelled) setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const [tplRes, statRes, branchRes] = await Promise.all([
          needStructures ? salaryStructureService.listTemplates() : Promise.resolve(null),
          needStatutory ? salaryStructureService.getStatutoryConfig() : Promise.resolve(null),
          needBranches ? branchService.getBranches() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (needStructures) setStructures(templatesToStructures(tplRes?.data));
        if (needStatutory && statRes?.data?.configJson) {
          setStatutoryConfigJson(statRes.data.configJson);
        }
        if (needBranches && branchRes?.success) {
          setBranchesList(branchRes.data || []);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || "Failed to load salary structures");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [structuresProp, statutoryProp, branchesProp, row?.employeeId]);

  useEffect(() => {
    let cancelled = false;
    const eid = row?.employeeId;
    if (!eid) return undefined;
    (async () => {
      try {
        const res = await salaryStructureService.getActiveStructure(eid);
        const data = res?.data || null;
        let params = data?.parametersJson ?? data?.parameters_json ?? null;
        if (typeof params === "string") {
          try {
            params = JSON.parse(params);
          } catch {
            params = null;
          }
        }
        const tid =
          data?.templateId ??
          data?.template_id ??
          params?.structureId ??
          detail?.templateId ??
          null;
        const appl =
          params?.statutoryApplicability || detail?.statutoryApplicability || null;
        if (!cancelled) {
          setActiveTemplateId(tid != null ? Number(tid) : null);
          if (appl) setStatutoryFromAssignment(appl);
        }
      } catch {
        if (!cancelled && detail?.templateId != null) {
          setActiveTemplateId(Number(detail.templateId));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row?.employeeId, detail?.templateId, detail?.statutoryApplicability]);

  const employee = useMemo(() => {
    const id = row?.employeeId;
    const fromList = (employeesProp || []).find(
      (e) => Number(e.id) === Number(id) || String(e.id) === String(id)
    );
    const appl = statutoryFromAssignment || {};
    const base = fromList || {
      id,
      empCode: row?.empCode,
      employeeCode: row?.empCode,
      ctc: detail?.revisedCtc ?? row?.newCtc,
    };
    return {
      ...base,
      pfApplicable: appl.pfApplicable ?? base.pfApplicable ?? true,
      ceilingPf: appl.ceilingPf ?? base.ceilingPf ?? true,
      esiApplicable: appl.esiApplicable ?? base.esiApplicable ?? false,
      ptApplicable: appl.ptApplicable ?? base.ptApplicable ?? true,
      gratuityApplicable: appl.gratuityApplicable ?? base.gratuityApplicable ?? false,
    };
  }, [employeesProp, row, detail, statutoryFromAssignment]);

  const months = detail?.months || [];

  const dayFactor = useMemo(() => sumArrearsDayFactor(months), [months]);

  const method =
    orgSalaryMethod ||
    getAuthJson("companyData")?.defaultSalaryStructureMethod ||
    "CTC";

  const structuresForEmployee = useMemo(() => {
    if (!activeTemplateId || !structures.length) return structures;
    const preferred = structures.find((s) => Number(s.id) === Number(activeTemplateId));
    if (!preferred) return structures;
    return [preferred, ...structures.filter((s) => Number(s.id) !== Number(activeTemplateId))];
  }, [structures, activeTemplateId]);

  const computed = useMemo(() => {
    if (loading) return null;
    // Prefer Process arrearsDetail rows (same differentials merged into payslip heads).
    const backendComps = Array.isArray(detail?.components) ? detail.components : [];
    if (backendComps.length) {
      const components = mapBackendComponents(backendComps);
      const monthComponentTotals = (detail?.monthComponentTotals || months || []).map(
        (seg) => {
          const rows = mapBackendComponents(seg.components || []);
          const grossDiff =
            seg.grossDiff != null
              ? Number(seg.grossDiff) || 0
              : round2(
                  rows
                    .filter((c) => String(c.section || "").startsWith("Earnings"))
                    .reduce((s, c) => s + Number(c.differential || 0), 0)
                );
          const deductionsDiff =
            seg.deductionsDiff != null
              ? Number(seg.deductionsDiff) || 0
              : round2(
                  rows
                    .filter((c) => String(c.section || "").startsWith("Deductions"))
                    .reduce((s, c) => s + Number(c.differential || 0), 0)
                );
          return {
            ...seg,
            components: rows,
            grossDiff,
            deductionsDiff,
            netPayDiff:
              seg.netPayDiff != null
                ? Number(seg.netPayDiff) || 0
                : round2(grossDiff - deductionsDiff),
            totalDifferential: grossDiff,
            monthCtcArrears:
              seg.monthCtcArrears != null
                ? seg.monthCtcArrears
                : round2(
                    (Number(detail?.monthlyDiff) || 0) *
                      resolveMonthArrearsFactor(seg || {})
                  ),
          };
        }
      );
      return {
        components,
        monthComponentTotals,
        structureName: detail?.structureName || null,
        dayFactor,
        error: null,
        source: "process_detail",
      };
    }
    const previousCtc = Number(detail?.previousCtc ?? row?.previousCtc) || 0;
    const revisedCtc = Number(detail?.revisedCtc ?? row?.newCtc) || 0;
    const eff = String(
      detail?.incrementEffectiveDate || row?.incrementEffectiveDate || row?.effectiveDate || ""
    ).slice(0, 7);
    return buildSalaryRevisionArrearsComponents({
      employee,
      previousCtc,
      revisedCtc,
      structures: structuresForEmployee,
      statutoryConfigJson,
      branchesList,
      orgSalaryMethod: method,
      dayFactor,
      months,
      effectiveMonth: eff || undefined,
      preferredStructureId: activeTemplateId,
    });
  }, [
    loading,
    detail,
    row,
    employee,
    structuresForEmployee,
    statutoryConfigJson,
    branchesList,
    method,
    dayFactor,
    months,
    activeTemplateId,
  ]);

  const visibleComponents = useMemo(() => {
    if (!computed) return [];
    if (monthTab === "all") return computed.components || [];
    const pack = (computed.monthComponentTotals || []).find((m) => m.payMonth === monthTab);
    return pack?.components || [];
  }, [computed, monthTab]);

  const monthPaidDays = useMemo(() => {
    if (monthTab === "all") return null;
    const pack = (computed?.monthComponentTotals || []).find((m) => m.payMonth === monthTab);
    if (pack?.paidDays != null && pack.paidDays !== "") return pack.paidDays;
    const m = months.find((x) => x.payMonth === monthTab);
    return m?.attendancePaidDays ?? m?.attendance?.paidDays ?? null;
  }, [monthTab, computed, months]);

  const sumSectionDiff = (prefix) =>
    round2(
      visibleComponents
        .filter((c) => String(c.section || "").startsWith(prefix))
        .reduce((s, c) => s + Number(c.differential || 0), 0)
    );

  const grossDiffTotal = useMemo(() => {
    if (monthTab !== "all") {
      const pack = (computed?.monthComponentTotals || []).find((m) => m.payMonth === monthTab);
      if (pack?.grossDiff != null) return Number(pack.grossDiff) || 0;
    }
    return sumSectionDiff("Earnings");
  }, [visibleComponents, monthTab, computed]);

  // Mid-month Option A: Process merges earnings only (tip PF/PT stay full month).
  const midMonthProcessOnly =
    computed?.source === "process_detail" &&
    Math.abs(Number(detail?.priorArrears) || 0) < 0.01 &&
    Math.abs(Number(detail?.midMonthAdjustment) || 0) >= 0.01;

  const deductionsDiffTotal = useMemo(() => {
    if (midMonthProcessOnly) return 0;
    if (monthTab !== "all") {
      const pack = (computed?.monthComponentTotals || []).find((m) => m.payMonth === monthTab);
      if (pack?.deductionsDiff != null) return Number(pack.deductionsDiff) || 0;
    }
    return sumSectionDiff("Deductions");
  }, [visibleComponents, monthTab, computed, midMonthProcessOnly]);

  // Deduction differential is "higher deduction" as positive → net = gross − deductions.
  const netPayDiffTotal = useMemo(
    () => round2(grossDiffTotal - deductionsDiffTotal),
    [grossDiffTotal, deductionsDiffTotal]
  );

  const ctcArrearsShown = useMemo(() => {
    if (monthTab === "all") {
      return Number(detail?.arrearsAmount ?? row?.arrearsAmount) || 0;
    }
    const pack = (computed?.monthComponentTotals || []).find((m) => m.payMonth === monthTab);
    if (pack?.monthCtcArrears != null) return Number(pack.monthCtcArrears) || 0;
    const m = months.find((x) => x.payMonth === monthTab);
    const monthlyDiff = Number(detail?.monthlyDiff) || 0;
    return round2(monthlyDiff * resolveMonthArrearsFactor(m || {}));
  }, [monthTab, detail, row, computed, months]);

  const monthTabLabel = monthTab === "all" ? "Consolidated" : formatPayMonthShort(monthTab);

  const componentRows = useMemo(() => {
    const rows = [];
    let last = null;
    visibleComponents.forEach((c, i) => {
      if (c.section !== last) {
        rows.push({ kind: "section", section: c.section, key: `sec-${c.section}-${monthTab}` });
        last = c.section;
      }
      rows.push({ kind: "component", ...c, key: `${monthTab}-${c.section}-${c.name}-${i}` });
    });
    return rows;
  }, [visibleComponents, monthTab]);

  if (!row || typeof document === "undefined") return null;

  const border = darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const sectionHead = darkMode ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]" : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-start justify-center bg-black/50 p-4 pt-16 pb-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="arrears-breakdown-title"
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${border}`}>
          <div>
            <h3 id="arrears-breakdown-title" className="text-sm font-semibold">
              {row.empCode || "—"} — {row.name || "Employee"} · Component arrears
            </h3>
            <p className={`text-xs ${muted}`}>
              {computed?.structureName
                ? `Structure: ${computed.structureName}`
                : detail?.structureName || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`rounded p-1.5 ${
              darkMode ? "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]" : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-xs">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {[
              ["Previous CTC", money(detail?.previousCtc ?? row?.previousCtc)],
              ["Revised CTC", money(detail?.revisedCtc ?? row?.newCtc)],
              [
                "Increment %",
                detail?.incrementPct != null ? `${detail.incrementPct}%` : "—",
              ],
              ["Consolidated arrears", money(detail?.arrearsAmount ?? row?.arrearsAmount)],
              [
                "Increment effective",
                String(
                  detail?.incrementEffectiveDate ||
                    row?.incrementEffectiveDate ||
                    row?.effectiveDate ||
                    "—"
                ).slice(0, 10),
              ],
            ].map(([label, val]) => (
              <div key={label} className={`rounded border p-2 ${border}`}>
                <p className={muted}>{label}</p>
                <p className="font-semibold tabular-nums">{val}</p>
              </div>
            ))}
          </div>

          <section>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide">Arrears Summary</h4>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setMonthTab("all")}
                  className={`rounded border px-2 py-1 text-[11px] ${
                    monthTab === "all"
                      ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/10 text-[hsl(var(--ds-primary))]"
                      : border
                  }`}
                >
                  Consolidated
                </button>
                {(computed?.monthComponentTotals || months).map((m) => (
                  <button
                    key={m.payMonth}
                    type="button"
                    onClick={() => setMonthTab(m.payMonth)}
                    className={`rounded border px-2 py-1 text-[11px] tabular-nums ${
                      monthTab === m.payMonth
                        ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/10 text-[hsl(var(--ds-primary))]"
                        : border
                    }`}
                  >
                    {formatPayMonthShort(m.payMonth)}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className={muted}>Loading salary structure components…</p>
            ) : loadError ? (
              <p className={`rounded-lg border border-dashed px-3 py-4 ${border} text-red-600`}>
                {loadError}
              </p>
            ) : computed?.error && !visibleComponents.length ? (
              <p className={`rounded-lg border border-dashed px-3 py-4 ${border} ${muted}`}>
                {computed.error}. CTC differential total is still{" "}
                {money(detail?.arrearsAmount ?? row?.arrearsAmount)}.
              </p>
            ) : (
              <div className={`overflow-auto rounded-lg border ${border}`}>
                <table className="w-full min-w-[640px]">
                  <thead className={darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}>
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Component</th>
                      <th className="px-2 py-1.5 text-right font-medium">Present</th>
                      <th className="px-2 py-1.5 text-right font-medium">Revised</th>
                      <th className="px-2 py-1.5 text-right font-medium">Differential</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthTab !== "all" ? (
                      <tr
                        className={
                          darkMode
                            ? "border-t border-[#3C3C3C] bg-[hsl(var(--ds-muted))]/40"
                            : "border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/80"
                        }
                      >
                        <td className="px-2 py-1.5 font-medium">Paid days</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {monthPaidDays != null ? monthPaidDays : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {monthPaidDays != null ? monthPaidDays : "—"}
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums ${muted}`}>—</td>
                      </tr>
                    ) : null}
                    {componentRows.map((r) => {
                      if (r.kind === "section") {
                        return (
                          <tr key={r.key} className={sectionHead}>
                            <td className="px-2 py-1.5 font-semibold" colSpan={4}>
                              {r.section}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr
                          key={r.key}
                          className={
                            darkMode ? "border-t border-[#3C3C3C]" : "border-t border-[hsl(var(--ds-border))]"
                          }
                        >
                          <td className="px-2 py-1.5 pl-4">{r.name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {money(r.present)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {money(r.revised)}
                          </td>
                          <td
                            className={`px-2 py-1.5 text-right tabular-nums font-medium ${signedClass(
                              r.differential,
                              darkMode
                            )}`}
                          >
                            {money(r.differential)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr
                      className={
                        darkMode
                          ? "border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                          : "border-t bg-[hsl(var(--ds-muted))]"
                      }
                    >
                      <td className="px-2 py-2 font-semibold" colSpan={3}>
                        {monthTab === "all"
                          ? "Gross total difference"
                          : `Gross total difference · ${monthTabLabel}`}
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums font-semibold ${signedClass(
                          grossDiffTotal,
                          darkMode
                        )}`}
                      >
                        {money(grossDiffTotal)}
                      </td>
                    </tr>
                    <tr className={darkMode ? "bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-surface))]"}>
                      <td className="px-2 py-2 font-semibold" colSpan={3}>
                        {monthTab === "all"
                          ? "Deductions total difference"
                          : `Deductions total difference · ${monthTabLabel}`}
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums font-semibold ${signedClass(
                          deductionsDiffTotal,
                          darkMode
                        )}`}
                      >
                        {money(deductionsDiffTotal)}
                      </td>
                    </tr>
                    <tr
                      className={
                        darkMode
                          ? "border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                          : "border-t bg-[hsl(var(--ds-muted))]"
                      }
                    >
                      <td className="px-2 py-2 font-semibold" colSpan={3}>
                        {monthTab === "all"
                          ? "Net pay difference"
                          : `Net pay difference · ${monthTabLabel}`}
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums font-semibold ${signedClass(
                          netPayDiffTotal,
                          darkMode
                        )}`}
                      >
                        {money(netPayDiffTotal)}
                      </td>
                    </tr>
                    <tr className={darkMode ? "bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-surface))]"}>
                      <td className={`px-2 py-2 ${muted}`} colSpan={3}>
                        {monthTab === "all"
                          ? "CTC arrears (payroll settlement)"
                          : `CTC arrears · ${monthTabLabel}`}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold">
                        {money(ctcArrearsShown)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
